/**
 * Support (AI support channel) — agent-side API.
 *
 * Backend: code/backend/src/modules/support/. Staff routes are protected by
 * @Roles(superAdmin/admin/coordinator); category writes are superAdmin-only.
 */
import { http, httpUpload } from "./client";

export interface SupportAttachment {
  key: string;
  filename: string;
  contentType: string;
  size: number;
}

// ===== Client-side (end user) =====

/** POST /support/chat — send a message and/or attachments to the AI support assistant. */
export const sendSupportChat = (
  message: string,
  ticketId?: string,
  attachments?: SupportAttachment[],
) =>
  http({
    method: "POST",
    url: "support/chat",
    data: {
      message,
      ...(ticketId ? { ticketId } : {}),
      ...(attachments?.length ? { attachments } : {}),
    },
    isToken: true,
  });

/** POST /support/upload — upload one chat attachment (multipart). Returns its metadata. */
export const uploadSupportFile = (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return httpUpload("support/upload", fd, "POST");
};

/** GET /support/tickets/:id/file?key=… — temporary presigned download URL for an attachment. */
export const fetchSupportFileUrl = (ticketId: string, key: string) =>
  http({ method: "GET", url: `support/tickets/${ticketId}/file`, params: { key }, isToken: true });

/** GET /support/my-conversation — the caller's own open support thread (or null). */
export const fetchMyConversation = () =>
  http({ method: "GET", url: "support/my-conversation", isToken: true });

/** GET /support/my-tickets — the caller's own tickets (open + closed), summary. */
export const fetchMyTickets = () =>
  http({ method: "GET", url: "support/my-tickets", isToken: true });

/** GET /support/my-unread — replies received after the user's last message (open tickets). */
export const fetchSupportMyUnread = () =>
  http({ method: "GET", url: "support/my-unread", isToken: true });

/** POST /support/tickets/:id/feedback — 👍/👎 on one AI answer (owner). value null clears it. */
export const sendSupportMessageFeedback = (
  ticketId: string,
  messageId: string,
  value: "up" | "down" | null,
) =>
  http({
    method: "POST",
    url: `support/tickets/${ticketId}/feedback`,
    data: { messageId, value },
    isToken: true,
  });

/** POST /support/tickets/:id/rate — rate a closed conversation 1–5 + optional comment (owner). */
export const rateSupportTicket = (ticketId: string, rating: number, comment?: string) =>
  http({
    method: "POST",
    url: `support/tickets/${ticketId}/rate`,
    data: { rating, ...(comment ? { comment } : {}) },
    isToken: true,
  });

// ===== Agent-side (staff) =====

/** GET /support/queue — paginated ticket queue (30/page), optionally by category. */
export const fetchSupportQueue = (category?: string, page = 1) =>
  http({
    method: "GET",
    url: "support/queue",
    params: { ...(category ? { category } : {}), page },
    isToken: true,
  });

/** GET /support/open-counts — number of OPEN tickets per category (for the filter badges). */
export const fetchSupportOpenCounts = () =>
  http({ method: "GET", url: "support/open-counts", isToken: true });

/** GET /support/admin/usage-cost — org spend/usage via Anthropic Admin API (superAdmin). */
export const fetchSupportUsageCost = (days = 30) =>
  http({ method: "GET", url: "support/admin/usage-cost", params: { days }, isToken: true });

/** GET /support/admin/metrics — AI dashboard metrics: topics, ratings, deflection (superAdmin). */
export const fetchSupportAiMetrics = (days = 30) =>
  http({ method: "GET", url: "support/admin/metrics", params: { days }, isToken: true });

/** GET /support/tickets/:id — the full ticket (messages). Owner or staff. */
export const fetchSupportTicket = (id: string) =>
  http({ method: "GET", url: `support/tickets/${id}`, isToken: true });

/** POST /support/tickets/:id/reply — agent replies (auto-assigns), optionally with attachments. */
export const replySupportTicket = (id: string, message: string, attachments?: SupportAttachment[]) =>
  http({
    method: "POST",
    url: `support/tickets/${id}/reply`,
    data: { message, ...(attachments?.length ? { attachments } : {}) },
    isToken: true,
  });

/** POST /support/tickets/:id/resolve — close the ticket. */
export const resolveSupportTicket = (id: string) =>
  http({ method: "POST", url: `support/tickets/${id}/resolve`, isToken: true });

/** POST /support/tickets/:id/reopen — undo a close (staff, within 10 min). */
export const reopenSupportTicket = (id: string) =>
  http({ method: "POST", url: `support/tickets/${id}/reopen`, isToken: true });

/** POST /support/tickets/:id/take — take over / claim (staff; category required from an AI ticket). */
export const takeSupportTicket = (id: string, category?: string) =>
  http({
    method: "POST",
    url: `support/tickets/${id}/take`,
    data: category ? { category } : {},
    isToken: true,
  });

// ===== Agent notification preferences (subscription per category) =====

/** GET /support/notify-prefs — my muted categories (subscription = not muted). */
export const fetchSupportNotifyPrefs = () =>
  http({ method: "GET", url: "support/notify-prefs", isToken: true });

/** PATCH /support/notify-prefs — save my muted categories. */
export const updateSupportNotifyPrefs = (mutedCategories: string[]) =>
  http({ method: "PATCH", url: "support/notify-prefs", data: { mutedCategories }, isToken: true });

/** GET /support/categories/subscribers — subscriber names per category (chip hover). */
export const fetchSupportCategorySubscribers = () =>
  http({ method: "GET", url: "support/categories/subscribers", isToken: true });

/** GET /support/my-alerts — pending-attention list, computed on Inbox entry (toast seeding). */
export const fetchSupportMyAlerts = () =>
  http({ method: "GET", url: "support/my-alerts", isToken: true });

// ===== Settings — "teach the assistant now" (chat config, superAdmin) =====

/** GET /support/settings — chat settings (incl. the teach-the-assistant notes). */
export const fetchSupportSettings = () =>
  http({ method: "GET", url: "support/settings", isToken: true });

/** PATCH /support/settings — update the teach-the-assistant notes (superAdmin). */
export const updateSupportSettings = (data: { teachNotes?: string }) =>
  http({ method: "PATCH", url: "support/settings", data, isToken: true });

// ===== Emergency contacts (shown to CLIENT users; managed by superAdmin) =====

/** GET /support/emergency-contacts — list (client users + superAdmin). */
export const fetchEmergencyContacts = () =>
  http({ method: "GET", url: "support/emergency-contacts", isToken: true });

/** POST /support/emergency-contacts — create (superAdmin). */
export const createEmergencyContact = (data: {
  name: string;
  phone: string;
  whenToCall?: string;
}) => http({ method: "POST", url: "support/emergency-contacts", data, isToken: true });

/** PATCH /support/emergency-contacts/:id — edit (superAdmin). */
export const updateEmergencyContact = (
  id: string,
  data: { name?: string; phone?: string; whenToCall?: string },
) => http({ method: "PATCH", url: `support/emergency-contacts/${id}`, data, isToken: true });

/** DELETE /support/emergency-contacts/:id — remove (superAdmin). */
export const deleteEmergencyContact = (id: string) =>
  http({ method: "DELETE", url: `support/emergency-contacts/${id}`, isToken: true });

// ===== Categories (chat config — Miscellaneous → Support) =====

/** GET /support/categories — list (IA + General always present). */
export const fetchSupportCategories = () =>
  http({ method: "GET", url: "support/categories", isToken: true });

/** POST /support/categories — create a category (name + description). */
export const createSupportCategory = (data: { name: string; description?: string }) =>
  http({ method: "POST", url: "support/categories", data, isToken: true });

/** PATCH /support/categories/:id — edit name/description. */
export const updateSupportCategory = (id: string, data: { name?: string; description?: string }) =>
  http({ method: "PATCH", url: `support/categories/${id}`, data, isToken: true });

/** DELETE /support/categories/:id — remove (system categories blocked on the backend). */
export const deleteSupportCategory = (id: string) =>
  http({ method: "DELETE", url: `support/categories/${id}`, isToken: true });

/** PATCH /support/categories/reorder — persist a new order for custom categories. */
export const reorderSupportCategories = (ids: string[]) =>
  http({ method: "PATCH", url: "support/categories/reorder", data: { ids }, isToken: true });
